import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { xadmApi } from '../../api/xadm.js';
import { Spinner, ErrorBox, EmptyBox, Pagination } from '../../components/ui.js';
import { LineChart } from '../../components/LineChart.js';
import { useUrlUpdate } from '../../lib/hooks.js';
import { num } from '../../lib/format.js';

/**
 * 배송현황 — 원본 /home/bananapartner/xadm/delivery_list.php.
 *
 * 정각 스냅샷과 요일별 페이지네이션 규칙은 유지하면서, 최근 처리 상태와 추세를
 * 먼저 파악하고 행 hover/클릭으로 분 단위 기록을 확인하도록 구성한다.
 */

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];
const WEEKDAY_NAMES = WEEKDAYS.map((day) => `${day}요일`);

function hourLabel(hour: number) {
  return `${String(hour).padStart(2, '0')}:00`;
}

function minuteLabel(hour: number, minute: number) {
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

function completionRate(done: number, total: number) {
  return total ? (done / total) * 100 : 0;
}

export function LogisticsPage() {
  const [params, setParams] = useSearchParams();
  const update = useUrlUpdate(params, setParams);
  const weekdayParam = params.get('weekday');
  const page = Number(params.get('page') ?? '1') || 1;
  const [hoverKey, setHoverKey] = useState<string | null>(null);
  const [pinnedKey, setPinnedKey] = useState<string | null>(null);
  const hoverCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const openKey = hoverKey ?? pinnedKey;

  const hoursPage = useQuery({
    queryKey: ['xadm-logistics-hours', weekdayParam, page],
    queryFn: () => xadmApi.logisticsHours(weekdayParam === null ? undefined : Number(weekdayParam), page),
  });

  const openRow = hoursPage.data?.rows.find((row) => rowKey(row.date, row.hour) === openKey) ?? null;
  const minutes = useQuery({
    queryKey: ['xadm-logistics-minutes', openRow?.date, openRow?.hour],
    queryFn: () => xadmApi.logisticsMinutes(openRow!.date, openRow!.hour),
    enabled: openRow !== null,
  });

  const selectedWeekday = weekdayParam === null ? (hoursPage.data?.weekday ?? null) : Number(weekdayParam);
  const summary = useMemo(() => {
    const rows = hoursPage.data?.rows ?? [];
    const latest = rows[0] ?? null;
    const trend = [...rows.slice(0, 24)].reverse();
    const averageRate = rows.length
      ? rows.reduce((sum, row) => sum + completionRate(row.done, row.total), 0) / rows.length
      : 0;
    return {
      rowCount: rows.length,
      latest,
      averageRate,
      peak: trend.length === 0 ? null : trend.reduce((max, row) => row.total > max.total ? row : max),
      trend,
    };
  }, [hoursPage.data]);

  // 전날 미처리 이월 - 전광판과 같은 숫자를 서버가 세어 준다. 오늘 행에만 붙는다.
  const carriedOver = hoursPage.data?.carriedOver ?? 0;

  const trendLabels = summary.trend.map((row) => `${row.date} ${String(row.hour).padStart(2, '0')}시`);
  const trendSeries = [
    { id: 'total', label: '전체', points: summary.trend.map((row) => ({ x: `${row.date} ${String(row.hour).padStart(2, '0')}시`, y: row.total })) },
    { id: 'done', label: '완료', points: summary.trend.map((row) => ({ x: `${row.date} ${String(row.hour).padStart(2, '0')}시`, y: row.done })) },
    { id: 'work', label: '송장', points: summary.trend.map((row) => ({ x: `${row.date} ${String(row.hour).padStart(2, '0')}시`, y: row.work })) },
  ];

  const cancelHoverClose = () => {
    if (hoverCloseTimer.current !== null) clearTimeout(hoverCloseTimer.current);
    hoverCloseTimer.current = null;
  };

  const previewRow = (key: string) => {
    cancelHoverClose();
    setHoverKey(key);
  };

  const schedulePreviewClose = () => {
    cancelHoverClose();
    hoverCloseTimer.current = setTimeout(() => setHoverKey(null), 180);
  };

  const togglePinnedRow = (key: string) => {
    cancelHoverClose();
    setHoverKey(null);
    setPinnedKey((current) => current === key ? null : key);
  };

  const closeDetail = () => {
    setHoverKey(null);
    setPinnedKey(null);
  };

  const selectWeekday = (weekday: number) => {
    closeDetail();
    update({ weekday, page: undefined });
  };

  const selectPage = (nextPage: number) => {
    closeDetail();
    update({ page: nextPage }, false);
  };

  useEffect(() => () => cancelHoverClose(), []);

  return (
    <section className="visit-page logistics-page">
      <header className="page-head visit-page__head">
        <div>
          <h1 className="page-title">배송 처리 통계</h1>
          <p className="page-desc">요일별 시간대 스냅샷에서 송장 처리량과 완료 흐름을 빠르게 탐색합니다.</p>
        </div>
        <span className="visit-page__source"><i aria-hidden="true" /> HOURLY LOGISTICS LOG</span>
      </header>

      <nav className="visit-weekdays" aria-label="조회 요일">
        <span className="visit-weekdays__label">요일 선택</span>
        <div className="visit-weekdays__buttons">
          {WEEKDAYS.map((label, weekday) => (
            <button
              key={weekday}
              type="button"
              className={weekday === selectedWeekday ? 'visit-weekday visit-weekday--active' : 'visit-weekday'}
              aria-pressed={weekday === selectedWeekday}
              title={WEEKDAY_NAMES[weekday]}
              onClick={() => selectWeekday(weekday)}
            >
              {label}
            </button>
          ))}
        </div>
        <p>선택한 요일의 정각 배송 스냅샷을 표시합니다.</p>
      </nav>

      {!hoursPage.isLoading && !hoursPage.isError && summary.rowCount > 0 && (
        <>
          <div className="visit-summary" aria-label="배송 처리 핵심 지표">
            <article className="visit-summary__item visit-summary__item--primary">
              <span>최근 전체</span>
              <strong>{num(summary.latest?.total ?? 0)}</strong>
              <small>{summary.latest?.date} · {hourLabel(summary.latest?.hour ?? 0)}</small>
            </article>
            <article className="visit-summary__item logistics-summary__done">
              <span>최근 완료</span>
              <strong>{num(summary.latest?.done ?? 0)}</strong>
              <small>완료율 {completionRate(summary.latest?.done ?? 0, summary.latest?.total ?? 0).toFixed(1)}%</small>
            </article>
            <article className="visit-summary__item logistics-summary__work">
              <span>최근 송장</span>
              <strong>{num(summary.latest?.work ?? 0)}</strong>
              <small>
                {summary.latest?.isToday && carriedOver > 0
                  ? `정각 기록 기준 · 전날 이월 ${num(carriedOver)}건 별도`
                  : '정각 기록 기준'}
              </small>
            </article>
            <article className="visit-summary__item logistics-summary__rate">
              <span>현재 페이지 평균 완료율</span>
              <strong>{summary.averageRate.toFixed(1)}%</strong>
              <small>{num(summary.rowCount)}개 스냅샷 기준</small>
            </article>
          </div>

          <section className="visit-trend logistics-trend" aria-label="최근 배송 처리 추이">
            <header className="visit-trend__head">
              <div>
                <span>TREND</span>
                <h2>최근 {summary.trend.length}개 시간대 처리 추이</h2>
                <p>현재 페이지의 최신 스냅샷을 시간순으로 비교합니다.</p>
              </div>
              {summary.peak && (
                <div className="visit-trend__peak">
                  <span>최근 최고 물량</span>
                  <strong>{num(summary.peak.total)}</strong>
                  <small>{summary.peak.date} · {hourLabel(summary.peak.hour)}</small>
                </div>
              )}
            </header>
            <LineChart series={trendSeries} labels={trendLabels} height={220} />
          </section>
        </>
      )}

      <div className="visit-list-head">
        <div>
          <h2>{selectedWeekday === null ? '배송 기록' : `${WEEKDAY_NAMES[selectedWeekday]} 배송 기록`}</h2>
          <p>시간대 행에 마우스를 올리면 오른쪽에서 분 단위 처리 변화를 확인할 수 있습니다.</p>
        </div>
        {!hoursPage.isLoading && !hoursPage.isError && <span>전체 {num(hoursPage.data?.total ?? 0)}건</span>}
      </div>

      {hoursPage.isLoading ? (
        <Spinner />
      ) : hoursPage.isError ? (
        <ErrorBox error={hoursPage.error} />
      ) : hoursPage.data!.rows.length === 0 ? (
        <EmptyBox />
      ) : (
        <>
          <div className="visit-table-wrap">
            <table className="table logistics-table">
              <thead>
                <tr>
                  <th aria-label="상세 열기" />
                  <th>일자</th>
                  <th>요일</th>
                  <th>시간대</th>
                  <th>처리 진행</th>
                  <th>전체</th>
                  <th>완료</th>
                  <th>송장</th>
                  <th>기록시간</th>
                </tr>
              </thead>
              <tbody>
                {hoursPage.data!.rows.map((row) => {
                  const key = rowKey(row.date, row.hour);
                  const isOpen = openKey === key;
                  const doneRate = completionRate(row.done, row.total);
                  return (
                    <tr
                      key={row.seq}
                      className={isOpen ? 'table__row--clickable visit-table__row visit-table__row--open' : 'table__row--clickable visit-table__row'}
                      onMouseEnter={() => previewRow(key)}
                      onMouseLeave={schedulePreviewClose}
                      onClick={() => togglePinnedRow(key)}
                    >
                      <td className="visit-table__toggle">
                        <button
                          type="button"
                          aria-label={`${row.date} ${hourLabel(row.hour)} 분 단위 상세 ${pinnedKey === key ? '닫기' : '고정해서 열기'}`}
                          aria-expanded={isOpen}
                          onClick={(event) => {
                            event.stopPropagation();
                            togglePinnedRow(key);
                          }}
                        >
                          <span aria-hidden="true">›</span>
                        </button>
                      </td>
                      <td className="visit-table__date">{row.date}</td>
                      <td><span className={`visit-week-badge visit-week-badge--${row.weekday}`}>{WEEKDAYS[row.weekday]}</span></td>
                      <td><span className="visit-hour">{hourLabel(row.hour)}</span></td>
                      <td>
                        <div className="logistics-progress" aria-label={`완료율 ${doneRate.toFixed(1)}%`}>
                          <div><i style={{ width: `${Math.min(100, doneRate)}%` }} /></div>
                          <small>{doneRate.toFixed(1)}%</small>
                        </div>
                      </td>
                      <td className="td-right logistics-table__total">{num(row.total)}</td>
                      <td className="td-right logistics-table__done">{num(row.done)}</td>
                      <td className="td-right logistics-table__work">
                        {num(row.work)}
                        {/* 기록된 스냅샷 값은 그대로 두고, 이월은 별도 배지로만 알린다. */}
                        {row.isToday && carriedOver > 0 && (
                          <span
                            className="logistics-carry"
                            title={`전날 발행됐지만 아직 출고 처리되지 않은 송장 ${num(carriedOver)}건`}
                          >
                            이월 +{num(carriedOver)}
                          </span>
                        )}
                      </td>
                      <td className="logistics-table__recorded">{row.recordedAt ?? '-'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <Pagination
            page={hoursPage.data!.page}
            totalPages={Math.max(1, Math.ceil(hoursPage.data!.total / hoursPage.data!.pageSize))}
            total={hoursPage.data!.total}
            onChange={selectPage}
          />
        </>
      )}

      {openRow && (
        <aside
          className="visit-preview logistics-preview"
          aria-label={`${openRow.date} ${hourLabel(openRow.hour)} 분 단위 배송 처리`}
          onMouseEnter={cancelHoverClose}
          onMouseLeave={schedulePreviewClose}
        >
          <section className="visit-detail">
            <header className="visit-detail__head">
              <div>
                <span>{WEEKDAY_NAMES[openRow.weekday]} · HOURLY DETAIL</span>
                <h3>{openRow.date} {hourLabel(openRow.hour)} 배송 처리</h3>
              </div>
              <div className="visit-detail__actions">
                <strong>전체 {num(openRow.total)} · 완료 {num(openRow.done)}</strong>
                <button type="button" aria-label="분 단위 배송 처리 닫기" onClick={closeDetail}>×</button>
              </div>
            </header>
            {minutes.isLoading ? (
              <Spinner />
            ) : minutes.isError ? (
              <ErrorBox error={minutes.error} />
            ) : (minutes.data?.minutes.length ?? 0) === 0 ? (
              <EmptyBox />
            ) : (
              <div className="visit-detail__table-wrap">
                <table className="table table--nested logistics-minute-table">
                  <thead>
                    <tr>
                      <th>시각</th>
                      <th>처리 진행</th>
                      <th>전체</th>
                      <th>완료</th>
                      <th>송장</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(minutes.data?.minutes ?? []).map((minute) => {
                      const doneRate = completionRate(minute.done, minute.total);
                      return (
                        <tr key={minute.minute}>
                          <td><span className="visit-hour">{minuteLabel(openRow.hour, minute.minute)}</span></td>
                          <td>
                            <div className="logistics-progress" aria-label={`완료율 ${doneRate.toFixed(1)}%`}>
                              <div><i style={{ width: `${Math.min(100, doneRate)}%` }} /></div>
                              <small>{doneRate.toFixed(1)}%</small>
                            </div>
                          </td>
                          <td className="td-right logistics-table__total">{num(minute.total)}</td>
                          <td className="td-right logistics-table__done">{num(minute.done)}</td>
                          <td className="td-right logistics-table__work">{num(minute.work)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </aside>
      )}
    </section>
  );
}

function rowKey(date: string, hour: number) {
  return `${date}:${hour}`;
}
